import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ContextSelectorModalService } from '../../../core/services/context-selector-modal.service';
import { AuthService } from '../../../core/services/auth.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { TenantContextService } from '../../../core/services/tenant-context.service';
import { TenantsStore } from '../../../core/stores/tenants.store';
import { AccessibleBusinessUnitStore } from '../../../core/stores/accessible-business-unit.store';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { LedgerCategoriesStore } from '../../../core/stores/ledger-categories.store';
import { BusinessUnitCustomFieldsStore } from '../../../core/stores/business-unit-custom-fields.store';
import { BusinessUnitService } from '../../../core/services/business-unit.service';
import { ReportService } from '../../../core/services/report.service';

interface BuOption {
  id: string;
  name: string;
  description?: string;
}

@Component({
  selector: 'app-context-selector-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent],
  templateUrl: './context-selector-modal.component.html',
  styles: [`
    :host { display: contents; }

    .ctx-backdrop {
      background: rgba(17, 24, 39, 0.55);
      backdrop-filter: blur(4px);
      animation: ctx-fade 160ms ease-out;
    }
    .ctx-panel {
      box-shadow: 0 24px 60px -12px rgba(17, 24, 39, 0.35), 0 0 0 1px rgba(17, 24, 39, 0.04);
      animation: ctx-panel-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .ctx-pop { animation: ctx-pop 120ms ease-out; }
    .ctx-busy { animation: ctx-fade 120ms ease-out; }

    .ctx-skeleton {
      background: linear-gradient(90deg, #eef1f5 25%, #f6f8fa 37%, #eef1f5 63%);
      background-size: 400% 100%;
      animation: ctx-shimmer 1.4s ease infinite;
    }

    @keyframes ctx-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes ctx-panel-in {
      from { opacity: 0; transform: translateY(12px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes ctx-pop {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ctx-shimmer { from { background-position: 100% 0; } to { background-position: 0 0; } }

    @media (prefers-reduced-motion: reduce) {
      .ctx-backdrop, .ctx-panel, .ctx-pop, .ctx-skeleton, .ctx-busy {
        animation: none !important;
      }
    }
  `],
})
export class ContextSelectorModalComponent implements AfterViewChecked, OnDestroy {
  private modalSvc = inject(ContextSelectorModalService);
  private auth     = inject(AuthService);
  private authApi  = inject(AuthenticationService);
  private ctx      = inject(TenantContextService);
  private tenants  = inject(TenantsStore);
  private buStore  = inject(AccessibleBusinessUnitStore);
  private businessUnitStore = inject(BusinessUnitStore);
  private ledgerCategoriesStore = inject(LedgerCategoriesStore);
  private customFieldsStore = inject(BusinessUnitCustomFieldsStore);
  private reportService = inject(ReportService);
  private buService = inject(BusinessUnitService);
  private router   = inject(Router);

  @ViewChild('panel') panelRef?: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('villageInput') villageInputRef?: ElementRef<HTMLInputElement>;

  readonly titleId = `ctx-selector-${Math.random().toString(36).slice(2, 9)}`;

  readonly isOpen      = this.modalSvc.isOpen;
  readonly forceBlock  = this.modalSvc.forceBlock;
  readonly canSwitchTenant = this.modalSvc.canSwitchTenant;

  readonly committedTenantId = computed(() => this.ctx.selectedTenantId());
  readonly committedBuId     = computed(() => this.ctx.selectedBusinessUnitId());

  /** Local working selection — does not mutate JWT until Switch is clicked. */
  readonly draftTenantId = signal<string | null>(null);
  readonly draftBuId     = signal<string | null>(null);

  readonly tenantOptions = computed(() => this.tenants.tenants());

  /** Village combobox state (typeahead). */
  readonly villageSearch          = signal('');
  readonly villageDropdownOpen    = signal(false);
  readonly villageHighlightIndex  = signal(0);

  readonly filteredVillages = computed(() => {
    const q = this.villageSearch().trim().toLowerCase();
    const list = this.tenantOptions();
    if (!q) return list;
    return list.filter(t => (t.villageName ?? '').toLowerCase().includes(q));
  });

  /** Value shown in the village input — typed text while open, committed name while closed. */
  readonly villageInputDisplay = computed(() => {
    if (this.villageDropdownOpen()) return this.villageSearch();
    const id = this.draftTenantId();
    if (!id) return '';
    return this.tenants.byId().get(id)?.villageName ?? '';
  });

  /** BUs for the *committed* tenant (used when user hasn't picked a different tenant). */
  readonly committedTenantBus = computed(() => this.buStore.activeUnits());

  /** Locally-fetched BUs for a non-committed tenant chosen inside the modal. */
  private _previewBus = signal<BuOption[]>([]);
  private _previewLoading = signal(false);
  private _previewTenantId = signal<string | null>(null);
  readonly previewLoading = this._previewLoading.asReadonly();

  readonly visibleBus = computed<BuOption[]>(() => {
    const draftT = this.draftTenantId();
    const committedT = this.committedTenantId();
    if (draftT && draftT !== committedT) {
      return this._previewBus();
    }
    return this.committedTenantBus().map(b => ({ id: b.id, name: b.name, description: b.description }));
  });

  readonly search = signal('');
  readonly filteredBus = computed<BuOption[]>(() => {
    const q = this.search().trim().toLowerCase();
    const list = this.visibleBus();
    if (!q) return list;
    return list.filter(b => b.name.toLowerCase().includes(q));
  });

  readonly errorMessage = signal<string | null>(null);
  readonly isApplying   = signal(false);
  readonly applyStep    = signal<'idle' | 'tenant' | 'bu'>('idle');
  /** Captured at apply() time so the retry path knows whether the village changed. */
  private _pendingTenantChanged = false;

  readonly tenantUnchanged = computed(() => {
    const draft = this.draftTenantId();
    return draft === null || draft === this.committedTenantId();
  });

  readonly buUnchanged = computed(() => {
    const draft = this.draftBuId();
    return draft === null || draft === this.committedBuId();
  });

  readonly canApply = computed(() => {
    if (this.isApplying()) return false;
    const buId = this.draftBuId();
    if (!buId) return false;
    if (!this.tenantUnchanged() && !this.draftTenantId()) return false;
    if (this.tenantUnchanged() && this.buUnchanged()) return false;
    return true;
  });

  readonly currentTenantName = computed(() => {
    const id = this.committedTenantId();
    if (!id) return '';
    return this.tenants.byId().get(id)?.villageName || this.auth.tenant()?.tenantName || '';
  });

  readonly draftTenantName = computed(() => {
    const id = this.draftTenantId();
    if (!id) return this.currentTenantName();
    return this.tenants.byId().get(id)?.villageName || id;
  });

  /** Name of the committee currently committed in the JWT (for the "current workspace" row). */
  readonly currentBuName = computed(() => {
    const id = this.committedBuId();
    if (id == null) return '';
    return this.buStore.byId().get(id)?.name || this.auth.tenant()?.businessUnitName || '';
  });

  /** True once a village and committee are staged and differ from the current context. */
  readonly hasCurrentContext = computed(() =>
    this.committedTenantId() != null && this.committedBuId() != null
  );

  /** Name of the committee staged in the draft (for the success confirmation). */
  readonly draftBuName = computed(() => {
    const id = this.draftBuId();
    if (id == null) return '';
    return this.visibleBus().find(b => b.id === id)?.name || '';
  });

  private bodyOverflowBefore: string | null = null;
  private lastActiveElement: HTMLElement | null = null;
  private focusPending = false;

  constructor() {
    // Seed draft state when the modal opens; clear it when it closes.
    effect(() => {
      const open = this.isOpen();
      if (open) {
        this.lockBodyScroll();
        this.lastActiveElement = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
        this.draftTenantId.set(this.committedTenantId());
        this.draftBuId.set(this.committedBuId());
        this.search.set('');
        this.villageSearch.set('');
        this.villageDropdownOpen.set(false);
        this.villageHighlightIndex.set(0);
        this.errorMessage.set(null);
        this.applyStep.set('idle');
        this._previewBus.set([]);
        this._previewTenantId.set(null);
        // Super admin with no committed tenant + exactly one village: pre-select
        // it so they only have to pick a committee (loads that tenant's BUs).
        if (this.committedTenantId() == null) {
          const onlyTenant = this.tenants.tenants();
          if (onlyTenant.length === 1) {
            this.onTenantChange(onlyTenant[0].tenantId);
          }
        }
        this.focusPending = true;
      } else {
        this.restoreBodyScroll();
        this.restoreFocus();
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.focusPending && this.isOpen()) {
      const el = this.villageInputRef?.nativeElement
        ?? this.searchInputRef?.nativeElement
        ?? this.panelRef?.nativeElement?.querySelector<HTMLElement>('[data-initial-focus]');
      if (el) {
        el.focus();
        this.focusPending = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.restoreBodyScroll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen() || this.forceBlock() || this.isApplying()) return;
    this.modalSvc.close();
  }

  onTenantChange(tenantId: string): void {
    if (!tenantId) return;
    this.draftTenantId.set(tenantId);
    // If user goes back to the committed tenant, drop the BU draft only if it's no longer valid.
    if (tenantId === this.committedTenantId()) {
      this._previewBus.set([]);
      this._previewTenantId.set(null);
      const draftBu = this.draftBuId();
      const stillValid = this.committedTenantBus().some(b => b.id === draftBu);
      if (!stillValid) this.draftBuId.set(this.committedBuId());
      return;
    }
    // Different tenant — clear BU draft and fetch that tenant's BUs.
    this.draftBuId.set(null);
    this.loadPreviewBus(tenantId);
  }

  // -------- Village combobox --------

  openVillageDropdown(): void {
    if (this.isApplying()) return;
    if (this.tenantOptions().length === 0) return;
    this.villageSearch.set('');
    this.villageHighlightIndex.set(0);
    this.villageDropdownOpen.set(true);
  }

  onVillageInputClick(): void {
    if (!this.villageDropdownOpen()) this.openVillageDropdown();
  }

  closeVillageDropdown(): void {
    if (!this.villageDropdownOpen()) return;
    this.villageDropdownOpen.set(false);
    this.villageSearch.set('');
  }

  onVillageInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.villageSearch.set(value);
    this.villageHighlightIndex.set(0);
    if (!this.villageDropdownOpen()) this.villageDropdownOpen.set(true);
  }

  pickVillage(tenantId: string, displayName: string): void {
    this.onTenantChange(tenantId);
    this.villageSearch.set(displayName);
    this.villageDropdownOpen.set(false);
  }

  onVillageInputKeydown(event: KeyboardEvent): void {
    const list = this.filteredVillages();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.villageDropdownOpen()) {
          this.villageDropdownOpen.set(true);
          this.villageHighlightIndex.set(0);
          return;
        }
        if (list.length === 0) return;
        this.villageHighlightIndex.set((this.villageHighlightIndex() + 1) % list.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (list.length === 0) return;
        this.villageHighlightIndex.set(
          (this.villageHighlightIndex() - 1 + list.length) % list.length,
        );
        return;
      case 'Enter': {
        if (!this.villageDropdownOpen()) return;
        event.preventDefault();
        const idx = this.villageHighlightIndex();
        const choice = list[idx];
        if (choice) this.pickVillage(choice.tenantId, choice.villageName);
        return;
      }
      case 'Escape':
        if (this.villageDropdownOpen()) {
          event.preventDefault();
          event.stopPropagation();
          this.closeVillageDropdown();
        }
        return;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.villageDropdownOpen()) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest('[data-village-combobox]')) return;
    this.closeVillageDropdown();
  }

  private loadPreviewBus(tenantId: string): void {
    this._previewTenantId.set(tenantId);
    this._previewLoading.set(true);
    this._previewBus.set([]);
    this.buService.getBusinessUnitsByTenant(tenantId).subscribe({
      next: (res: any) => {
        // Defensive against either Ok(data) or raw payload.
        const raw = res?.data ?? res;
        const list: any[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
        const mapped: BuOption[] = list
          .filter(x => x?.isActive !== false)
          .map(x => ({
            id: x.id,
            name: x.name,
            description: x.description,
          }))
          .filter(x => typeof x.id === 'string' && x.id.length > 0);
        // Bail if user moved on while the request was in flight.
        if (this._previewTenantId() === tenantId) {
          this._previewBus.set(mapped);
        }
      },
      error: () => {
        if (this._previewTenantId() === tenantId) {
          this.errorMessage.set('Could not load committees for that village.');
        }
      },
      complete: () => {
        if (this._previewTenantId() === tenantId) {
          this._previewLoading.set(false);
        }
      },
    });
  }

  selectBu(buId: string): void {
    if (this.isApplying()) return;
    this.draftBuId.set(buId);
  }

  cancel(): void {
    if (this.forceBlock() || this.isApplying()) return;
    this.modalSvc.close();
  }

  apply(): void {
    if (!this.canApply()) return;
    const targetTenant = this.draftTenantId();
    const targetBu = this.draftBuId();
    if (!targetBu) return;

    this.errorMessage.set(null);
    this.isApplying.set(true);
    this.modalSvc.beginSwitch();

    const tenantChanged = targetTenant !== null && targetTenant !== this.committedTenantId();
    this._pendingTenantChanged = tenantChanged;
    if (tenantChanged) {
      this.applyStep.set('tenant');
      this.authApi.switchTenant(targetTenant!).subscribe({
        next: () => this.switchBu(targetBu),
        error: () => {
          this.isApplying.set(false);
          this.applyStep.set('idle');
          this.modalSvc.endSwitch();
          this.errorMessage.set('Could not switch village. Please try again.');
        },
      });
    } else {
      this.switchBu(targetBu);
    }
  }

  /** Retry only the BU step (used after a partial failure). */
  retryBu(): void {
    const targetBu = this.draftBuId();
    if (!targetBu) return;
    this.errorMessage.set(null);
    this.isApplying.set(true);
    this.modalSvc.beginSwitch();
    this.switchBu(targetBu);
  }

  private switchBu(buId: string): void {
    this.applyStep.set('bu');
    this.authApi.switchBusinessUnit(buId).subscribe({
      next: () => this.finalizeSwitch(),
      error: () => {
        this.isApplying.set(false);
        this.applyStep.set('idle');
        this.modalSvc.endSwitch();
        this.errorMessage.set(
          'Switched village but could not switch committee. Tap Retry, or cancel and sign in again.'
        );
      },
    });
  }

  /**
   * Finish an in-place switch — no full page reload. The switch API reissued the
   * JWT via AuthService.setToken(), so the tenant/BU context signals are already
   * current and every reactive page re-fetches on its own. We only need to refresh
   * the two tenant-scoped caches that have no tenant key, then land on /home.
   *
   * The modal is closed *before* anything renders again so there is no flash of
   * the old panel; auto-open stays suppressed until navigation settles.
   */
  private finalizeSwitch(): void {
    if (this._pendingTenantChanged) {
      // These caches key on a plain `_loaded` flag, so a village switch would
      // otherwise keep serving the previous tenant's committees.
      this.buStore.reset();
      this.businessUnitStore.reset();
      this.ledgerCategoriesStore.reset();
      this.customFieldsStore.reset();
      this.reportService.invalidateCache();
      this.buStore.loadOnce();           // scope=mine — feeds the pill + this modal
      this.businessUnitStore.loadOnce(); // admin BU list + byId() name lookups
    }
    this._pendingTenantChanged = false;
    this.applyStep.set('idle');
    this.isApplying.set(false);
    // Hold auto-open closed across the navigation + store-reset settle window, then
    // release the immediate flag. The time window (not the nav promise) is what
    // actually prevents the forced modal from re-flashing when already on /home.
    this.modalSvc.markSwitched();
    this.modalSvc.forceClose();
    this.router.navigate(['/home']).then(() => this.modalSvc.endSwitch());
  }

  logoutFromModal(): void {
    this.authApi.logout();
    this.modalSvc.forceClose();
    this.router.navigate(['/login']);
  }

  isBuSelected(id: string): boolean {
    return this.draftBuId() === id;
  }

  trackBu(_i: number, b: BuOption): string {
    return b.id;
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined') return;
    if (this.bodyOverflowBefore === null) {
      this.bodyOverflowBefore = document.body.style.overflow;
    }
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    if (typeof document === 'undefined') return;
    if (this.bodyOverflowBefore !== null) {
      document.body.style.overflow = this.bodyOverflowBefore;
      this.bodyOverflowBefore = null;
    }
  }

  private restoreFocus(): void {
    if (this.lastActiveElement && typeof this.lastActiveElement.focus === 'function') {
      try { this.lastActiveElement.focus(); } catch { /* ignore */ }
    }
    this.lastActiveElement = null;
  }
}
